import {
  AfterViewInit,
  Component,
  EventEmitter,
  Output,
  ViewChild,
} from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import * as _ from "lodash";
import * as moment from "moment";
import { DataTable, SelectItem } from "primeng/primeng";
import { AuditHistory } from "../../models/auditHistory.model";
import { Claim } from "../../models/claim.model";
import { EventId } from "../../models/eventId.model";
import { ManualApplicationPayment } from "../../models/manualApplicationPayment.model";
import { PostedClaimGLAdjustment } from "../../models/postedClaimGLAdjustment.model";
import { PostedClaimReversal } from "../../models/postedClaimReversal.model";
import { PostedRemittanceGLAdjustment } from "../../models/postedRemittanceGLAdjustment.model";
import { Remittance } from "../../models/remittance.model";
import { RemittanceSplit } from "../../models/remittanceSplit.model";
import { ReversalTransaction } from "../../models/reversalTransaction.model";
import { ServiceEventAdvancedSearchCriteria } from "../../models/serviceEventAdvancedSearchCriteria.model";
import {
  ServiceEventItemType,
  ServiceEventReassign,
} from "../../models/serviceEventReassign.model";
import { ServiceEventTransactionHistoryItem } from "../../models/serviceEventTransactionHistoryItem.model";
import { ApplicationService } from "../../services/application.service";
import { AuthorizationService } from "../../services/authorization.service";
import { GLPolicyService } from "../../services/gl-policy.service";
import { GlobalFilterService } from "../../services/global-filter.service";
import {
  EventSearchType,
  ReconApiSearchService,
} from "../../services/recon-api.search.service";
import { EventSearchApplicationSelectionComponent } from "./eventsearch-applicationselection/eventsearch-applicationselection.component";
import { EventSearchApplicationHelper } from "./helpers/event-search.applicationHelper";
import { EventIdHelper } from "./helpers/event-search.eventIdHelper";
import { EventSearchTableHelper } from "./helpers/event-search.tableHelper";

const STATUS_COL_INDEX = 0;

@Component({
  selector: "app-event-search",
  templateUrl: "./event-search.component.html",
  styleUrls: ["./event-search.component.css"],
})
export class EventSearchComponent implements AfterViewInit {
  @ViewChild("postedClaimsdDt") postedClaimsDataTable: DataTable;
  @ViewChild("postedRemittancesDt") postedRemittancesDataTable: DataTable;
  @ViewChild("transactionHistoryDt") transactionHistoryDataTable: DataTable;
  @ViewChild("applicationSelection")
  applicationSelection: EventSearchApplicationSelectionComponent;

  public readonly tableHelper: EventSearchTableHelper;
  public readonly applicationHelper: EventSearchApplicationHelper;

  public readonly disableSelectionAmount: 0.0;
  public postedClaims: Claim[] = [];
  public postedClaimsLoading: boolean;
  public postedClaimsOptions: SelectItem[];
  public unpostedClaims: Claim[] = [];
  public unpostedClaimsLoading: boolean;
  public postedRemittances: Remittance[] = [];
  public postedRemittancesLoading: boolean;
  public unpostedRemittances: Remittance[] = [];
  public unpostedRemittancesLoading: boolean;
  public adjustmentCodes: any[];
  public auditHistory: AuditHistory[];
  public auditHistoryLoading: boolean;
  public eventId: string;
  public adjustmentLoading: boolean;
  public adjStyle = { width: "95%", margin: "auto" };
  public yearRange = `1970:${new Date().getFullYear()}`;
  public transactionHistoryItems: ServiceEventTransactionHistoryItem[] = [];
  public transactionHistoryItemsLoading: boolean;
  public transHistoryARBalance: number;
  public transHistoryUACBalance: number;
  public searchValidationError: boolean;
  public searchValidationErrorMessage: string;
  public advancedSearchEnabled: boolean; // is the advancedSearch submit button enabled?
  public advancedSearchCriteria: ServiceEventAdvancedSearchCriteria;
  // stores the official state of the search criteria, in case someone changes a
  // field and doesn't submit the search, but they do a reassignment. We can use
  // these unbound values to guarantee we go back to the last official search values
  public advancedSearchCriteriaCurrentState: ServiceEventAdvancedSearchCriteria;
  public listOfReassigns: ServiceEventReassign[] = [];

  // ApplicationType members
  public applyApplication: boolean;
  public applicationValid: boolean;
  public options: any[];
  public manualApplicationModal: boolean;
  public reverseTransactionModal: boolean;
  public remittanceSplitModal: boolean;
  public postedClaimGLAdjustmentModal: boolean;
  public postedRemittanceGLAdjustmentModal: boolean;
  public postedRemittanceSEIDUpdateModal: boolean;
  public postedClaimReversalModal: boolean;
  public selectedClaim: Claim;
  public selectedRemittance: Remittance;
  public selectedAction: string;
  public selectedTransactionHistoryItem: ServiceEventTransactionHistoryItem;

  public eventLoaded = true;
  public advancedSearchVisible = false;

  public adjCodeDetailsVisible = false;
  public eventSearchForm: FormGroup;
  public eventIdModel: EventId;
  public formValidationMessages = {
    required: "This Field is Required!",
  };
  public firstReversal: boolean;
  public firstSearch: boolean;

  constructor(
    private authorizationService: AuthorizationService,
    private globalFilterService: GlobalFilterService,
    private searchService: ReconApiSearchService,
    private applicationService: ApplicationService,
    private glPolicyService: GLPolicyService,
    private fb: FormBuilder,
  ) {
    this.transHistoryARBalance = null;
    this.transHistoryUACBalance = null;
    this.eventIdModel = new EventId();
    this.tableHelper = new EventSearchTableHelper();
    this.applicationHelper = new EventSearchApplicationHelper(
      this.authorizationService,
    );
    this.options = this.applicationHelper.options;
    this.initForm();
    this.advancedSearchCriteria = new ServiceEventAdvancedSearchCriteria();
    this.advancedSearchCriteriaCurrentState =
      new ServiceEventAdvancedSearchCriteria();
    this.loadDefaultAdvancedSearchCriteria();
    this.listOfReassigns = [];
  }

  initForm(): void {
    this.eventSearchForm = this.fb.group({
      ncpdp: new FormControl({ disabled: true }, [
        Validators.required,
        Validators.minLength(7),
        Validators.maxLength(7),
        Validators.pattern("[0-9]{7}"),
      ]),
      prescriptionNumber: new FormControl({ disabled: true }, [
        Validators.required,
        Validators.maxLength(12),
      ]),
      fillDate: new FormControl(null, [Validators.required]),
    });
    this.eventSearchForm.controls["ncpdp"].valueChanges.subscribe(
      (selectedValue: string) => this.onCriteriaChange(selectedValue),
    );
  }

  onCriteriaChange(value?: string): void {
    //  Try to parse the value into an SEID
    let eventId = new EventIdHelper().toEventId(value);

    //  If we got a valid SEID, then update the form with the
    //  parsed (split) value.
    if (eventId && eventId.eventValid) {
      this.eventIdModel = eventId;
      let filldate = moment(this.eventIdModel.fillDate);
      this.eventSearchForm.setValue({
        ncpdp: this.eventIdModel.ncpdp,
        prescriptionNumber: this.eventIdModel.prescriptionNumber,
        fillDate: filldate.toDate(),
      });
    }
  }

  resetApplicationSelection() {
    if (this.applicationSelection) {
      this.applicationSelection.reset();
    }
  }

  public get DateTypeDictionaryKeys(): string[] {
    return Object.keys(this.advancedSearchCriteria.DateTypeDictionary);
  }

  UpdateAssignColumnVisibilityForPostedClaims() {
    let index = _.findIndex(
      this.tableHelper.PostedClaimsColumns,
      (o) => o.field === "IsRelatedItem",
    );
    this.tableHelper.PostedClaimsColumns[index].show =
      this.postedClaimsContainsRelatedClaims() ? true : false;
  }
  UpdateAssignColumnVisibilityForPostedRemittances() {
    let index = _.findIndex(
      this.tableHelper.PostedRemittanceColumns,
      (o) => o.field === "IsRelatedItem",
    );
    this.tableHelper.PostedRemittanceColumns[index].show =
      this.postedRemittancesContainsRelatedRemittances() ? true : false;
  }

  UpdateAssignColumnVisibilityForUnpostedClaims() {
    let index = _.findIndex(
      this.tableHelper.UnpostedClaimsColumns,
      (o) => o.field === "IsRelatedItem",
    );
    this.tableHelper.UnpostedClaimsColumns[index].show = false;
    // We're not going to be showing the reassign for unposted yet
    // this.tableHelper.UnpostedClaimsColumns[index].show = this.unpostedClaimsContainsRelatedClaims() ? true : false;
  }

  UpdateAssignColumnVisibilityForUnpostedRemittances() {
    let index = _.findIndex(
      this.tableHelper.UnpostedRemittanceColumns,
      (o) => o.field === "IsRelatedItem",
    );
    this.tableHelper.UnpostedRemittanceColumns[index].show = false;
    // We're not going to be showing the reassign for unposted yet
    // this.tableHelper.UnpostedRemittanceColumns[index].show = this.unpostedRemittancesContainsRelatedRemittances() ? true : false;
  }

  HideStatusColumn() {
    this.tableHelper.PostedRemittanceColumns[STATUS_COL_INDEX].show = false;
    this.tableHelper.PostedClaimsColumns[STATUS_COL_INDEX].show = false;
    this.tableHelper.TransactionHistoryColumns[STATUS_COL_INDEX].show = false;
  }

  ngAfterViewInit() {
    _.delay(() => {
      this.UpdateAssignColumnVisibilityForPostedClaims();
      this.UpdateAssignColumnVisibilityForPostedRemittances();
      this.UpdateAssignColumnVisibilityForUnpostedClaims();
      this.UpdateAssignColumnVisibilityForUnpostedRemittances();
      this.HideStatusColumn();
    }, 100);
  }

  loadDefaultAdvancedSearchCriteria() {
    // Set the default values for the AdvancedSearch. This might come from a db table later on
    // so that different clients could have different defaults.
    this.advancedSearchCriteria.DayVariance = "7";
    this.advancedSearchCriteria.MaxDays = 30;
    this.advancedSearchCriteria.DateType = "1";
    this.advancedSearchCriteria.NCPDPMatch = true;
    this.advancedSearchCriteria.RxMatch = true;
    this.advancedSearchCriteria.CarrierOrCarrierGroupMatch = false;
    this.advancedSearchCriteria.DayVarianceArray = [];
    // load an array of days to bind to UI dropdown
    for (let i = 0; i < this.advancedSearchCriteria.MaxDays; i++) {
      this.advancedSearchCriteria.DayVarianceArray.push((i + 1).toString());
    }

    this.setAdvancedSearchEnabled();
    this.setSearchCriteriaCurrentState();
    this.searchValidationError = false;
    this.searchValidationErrorMessage = "";
  }

  async resetAdvancedSearch() {
    this.loadDefaultAdvancedSearchCriteria();
    await this.loadData(this.eventIdModel.eventId).then(() => {
      this.updateLoadingState(false);
    });
  }

  private setSearchCriteriaCurrentState() {
    this.advancedSearchCriteriaCurrentState = this.advancedSearchCriteria;
  }

  // rebase the search criteria to the last time a search was peformed in case values are dirty
  private rebaseSearchCriteria() {
    this.advancedSearchCriteria = this.advancedSearchCriteriaCurrentState;
  }

  async loadData(
    eventId: string,
    isAdvancedSearch: boolean = false,
  ): Promise<void> {
    this.eventLoaded = false;

    this.eventId = this.eventIdModel.eventId;
    this.updateLoadingState(true);
    this.cleanDataArrays();
    this.resetApplicationType();

    // posted
    this.postedClaims = await this.getClaims(null, true, isAdvancedSearch);
    setTimeout(() => {
      this.UpdateAssignColumnVisibilityForPostedClaims();
    });
    this.postedRemittances = await this.getRemittances(
      null,
      true,
      isAdvancedSearch,
    );
    setTimeout(() => {
      this.UpdateAssignColumnVisibilityForPostedRemittances();
    });

    // unposted
    this.unpostedClaims = await this.getClaims(null, false, isAdvancedSearch);
    setTimeout(() => {
      this.UpdateAssignColumnVisibilityForUnpostedClaims();
    });
    this.unpostedRemittances = await this.getRemittances(
      null,
      false,
      isAdvancedSearch,
    );
    setTimeout(() => {
      this.UpdateAssignColumnVisibilityForUnpostedRemittances();
    });

    // transactionHistory
    this.transactionHistoryItems = await this.getTransactionHistory();

    setTimeout(() => {
      this.HideStatusColumn();
    });

    this.eventLoaded = true;
  }

  // Begin Actions functions
  private showPostFailureMessage() {
    toastr.error(this.applicationHelper.MessageFailure);
  }

  private showInfoMessage() {
    toastr.info("Request Submitted Successfully");
  }

  onOptionsChange(event) {
    this.selectedAction = event;
    this.resetApplicationType();
    this.applyApplication = true;

    switch (this.selectedAction) {
      case "ManualApplication":
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.PostedClaimsColumns,
        );
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.PostedRemittanceColumns,
        );
        break;
      case "RemittanceSplit":
      case "PostedRemittanceGLAdjustment":
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.PostedRemittanceColumns,
        );
        break;
      case "PostedRemittanceSEIDUpdate":
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.PostedRemittanceColumns,
        );
        break;
      case "ReversalTransaction":
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.TransactionHistoryColumns,
        );
        break;
      case "PostedClaimReversal":
      case "PostedClaimGLAdjustment":
        this.tableHelper.showColumn(
          STATUS_COL_INDEX,
          this.tableHelper.PostedClaimsColumns,
        );
        break;
    }
  }

  resetApplicationType() {
    this.applyApplication = false;
    this.applicationValid = false;
    this.manualApplicationModal = false;
    this.remittanceSplitModal = false;
    this.reverseTransactionModal = false;
    this.postedClaimGLAdjustmentModal = false;
    this.postedRemittanceGLAdjustmentModal = false;
    this.postedRemittanceSEIDUpdateModal = false;
    this.postedClaimReversalModal = false;
    this.selectedClaim = null;
    this.selectedRemittance = null;
    this.selectedTransactionHistoryItem = null;

    this.tableHelper.uncheckRows(this.postedClaimsDataTable);
    this.tableHelper.uncheckRows(this.postedRemittancesDataTable);
    this.tableHelper.uncheckRows(this.transactionHistoryDataTable);

    this.tableHelper.hideColumn(
      STATUS_COL_INDEX,
      this.tableHelper.PostedClaimsColumns,
    );
    this.tableHelper.hideColumn(
      STATUS_COL_INDEX,
      this.tableHelper.PostedRemittanceColumns,
    );
    this.tableHelper.hideColumn(
      STATUS_COL_INDEX,
      this.tableHelper.TransactionHistoryColumns,
    );
  }

  updatePostedClaimCheckboxState(rowData: any): boolean {
    if (this.applyApplication) {
      if (rowData.status) {
        return false;
      }

      if (this.selectedClaim) {
        return true;
      }

      // Any Posted Claim that is not a Transaction code of '001' should be disabled.
      if (rowData["AREngine_TransactionCode"] !== "001") {
        return true;
      }

      //  Action - Specific logic
      switch (this.selectedAction) {
        case "PostedClaimReversal":
          // Transaction codes of '001' that have a reversal in the transaction history should be disabled.
          if (
            _.some(this.transactionHistoryItems, {
              RawDataImport_ShardClaimIdentifier:
                rowData["RawDataImport_ShardClaimIdentifier"],
              Application_TransactionType: "MA            ",
            })
          ) {
            return true;
          }
          break;

        case "PostedClaimGLAdjustment":
          if (rowData["Money_OpenAmountClaim"] === 0) {
            return true;
          }
          break;
      }
    }
    return false;
  }

  updatePostedRemittanceCheckboxState(rowData: any): boolean {
    // Any Posted Remittance that has a Unapplied Amount of 0 should be disabled.
    if (this.applyApplication) {
      if (rowData.status) {
        return false;
      }
      if (this.selectedRemittance) {
        return true;
      }

      // $0 remittances can only be selected for the PostedRemittanceSEIDUpdate action.
      if (
        rowData["AREngine_UnappliedAmount"] === 0 &&
        this.selectedAction !== "PostedRemittanceSEIDUpdate"
      ) {
        return true;
      }

      //  Action - Specific logic
      switch (this.selectedAction) {
        case "RemittanceSplit":
          if (
            rowData["Money_PaidAmount"] === rowData["AREngine_UnappliedAmount"]
          ) {
            return false;
          }
          break;
      }
    }
    return false;
  }

  updateTransactionHistoryCheckboxState(rowData: any): boolean {
    const allowedSelection = ["CA", "CR", "TL", "MA", "OW"];

    if (this.applyApplication) {
      if (rowData["IsReversed"] === true) {
        return true;
      }
      if (
        allowedSelection.indexOf(
          rowData["Application_TransactionType"].trim(),
        ) === -1
      ) {
        return true;
      }
      if (rowData.status) {
        return false;
      }

      if (this.selectedTransactionHistoryItem) {
        return true;
      }
    }
    return false;
  }

  rowChecked(state, tableName: string): void {
    if (tableName === "PostedClaims") {
      this.selectedClaim = _.filter(this.postedClaimsDataTable.value, {
        status: true,
      })[0];
    } else if (tableName === "PostedRemittance") {
      this.selectedRemittance = _.filter(
        this.postedRemittancesDataTable.value,
        { status: true },
      )[0];
    } else if (tableName === "TransactionHistory") {
      this.selectedTransactionHistoryItem = _.filter(
        this.transactionHistoryDataTable.value,
        { status: true },
      )[0];
    }

    switch (this.selectedAction) {
      case "ManualApplication":
        this.applicationValid =
          this.selectedRemittance && this.selectedClaim ? true : false;
        break;
      case "RemittanceSplit":
      case "PostedRemittanceGLAdjustment":
      case "PostedRemittanceSEIDUpdate":
        this.applicationValid = this.selectedRemittance ? true : false;
        break;
      case "ReversalTransaction":
        this.applicationValid = this.selectedTransactionHistoryItem
          ? true
          : false;
        break;
      case "PostedClaimGLAdjustment":
      case "PostedClaimReversal":
        this.applicationValid = this.selectedClaim ? true : false;
        break;
    }
  }

  async onManualApplicationApply(event: any) {
    if (event.type === "cancel") {
      this.manualApplicationModal = false;
    } else {
      let manualApplicationPayment = new ManualApplicationPayment(
        Number(
          this.globalFilterService.selectedEnvironment.ClientId4Digit.trim(),
        ),
        this.selectedClaim.RawDataImport_ShardClaimIdentifier,
        this.selectedRemittance.Payment_ReferenceNumber,
        event.value,
      );
      await this.applicationService
        .postManualApplication(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          manualApplicationPayment,
        )
        .then(async (response) => {
          if (!response) {
            this.showPostFailureMessage();
          } else {
            this.showInfoMessage();
          }
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        });
    }
  }

  async onSplitRemittanceApplicationApply(event: any) {
    if (event.type === "cancel") {
      this.remittanceSplitModal = false;
    } else {
      let payload: RemittanceSplit = new RemittanceSplit(
        this.selectedRemittance.Payment_ReferenceNumber,
        event.value,
      );

      await this.applicationService
        .postSplitRemittanceApplication(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          payload,
        )
        .then(async () => {
          this.showInfoMessage();
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        })
        .catch(() => {
          this.showPostFailureMessage();
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        });
    }
  }

  async onReverseTransactionApplicationApply(event: any) {
    if (event.type === "cancel") {
      this.reverseTransactionModal = false;
    } else {
      let reverseTransaction = new ReversalTransaction(
        this.selectedTransactionHistoryItem.Application_ReferenceNumber,
        this.selectedTransactionHistoryItem.Application_TransactionType.trim(),
      );
      await this.applicationService
        .postReverseTransactionApplication(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          reverseTransaction,
        )
        .then(async (response) => {
          if (!response) {
            this.showPostFailureMessage();
          } else {
            this.showInfoMessage();
          }
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        });
    }
  }

  async onPostedClaimReversalApply(event: any) {
    if (event.type === "cancel") {
      this.postedClaimReversalModal = false;
    } else {
      let postedClaimReversal = new PostedClaimReversal(
        this.selectedClaim.RawDataImport_ShardClaimIdentifier,
      );
      await this.applicationService
        .postPostedClaimReversal(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          postedClaimReversal,
        )
        .then(async () => {
          this.firstReversal = false;
          this.showInfoMessage();
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        })
        .catch(() => {
          this.showPostFailureMessage();
        });
    }
  }

  async onPostedClaimGLAdjustmentApply(event: any) {
    if (event.type === "cancel") {
      this.postedClaimGLAdjustmentModal = false;
    } else {
      let postedClaimGLAdjustment = new PostedClaimGLAdjustment(
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        this.selectedClaim.RawDataImport_ShardClaimIdentifier,
        event.value,
      );

      await this.glPolicyService
        .postClaimGLAdjustment(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          postedClaimGLAdjustment,
        )
        .then(async (response) => {
          this.showInfoMessage();
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        })
        .catch(() => {
          this.showPostFailureMessage();
        });
    }
  }

  async onPostedRemittanceGLAdjustmentApply(event: any) {
    if (event.type === "cancel") {
      this.postedRemittanceGLAdjustmentModal = false;
    } else {
      let postedRemittanceGLAdjustment = new PostedRemittanceGLAdjustment(
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        this.selectedRemittance.Payment_ReferenceNumber,
        event.value,
      );

      await this.glPolicyService
        .postRemittanceGLAdjustment(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          postedRemittanceGLAdjustment,
        )
        .then(async (response) => {
          this.showInfoMessage();
          this.loadData(this.eventId);
          this.resetApplicationSelection();
        })
        .catch(() => {
          this.showPostFailureMessage();
        });
    }
  }

  async onPostedRemittanceSEIDUpdateApply(event: any) {
    if (event.type === "cancel") {
      this.postedRemittanceSEIDUpdateModal = false;
    } else {
      let model: EventId = event.value;

      // 1) reassign the item that has been checked to the new SEID.
      this.listOfReassigns.push(
        new ServiceEventReassign(
          ServiceEventItemType.CASH,
          this.selectedRemittance.Payment_ReferenceNumber,
        ),
      );

      //  Note: We post the model from the request.
      await this.searchService
        .CashAndClaimsAssignment(
          model.eventId,
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          this.listOfReassigns,
        )
        .then(async (response) => {
          if (!response) {
            this.showPostFailureMessage();
          } else {
            this.showInfoMessage();
          }
          this.loadData(this.eventId);
          this.resetApplicationSelection();
          this.listOfReassigns = [];
        });
    }
  }

  setApplicationTypeApplyView() {
    switch (this.selectedAction) {
      case "ManualApplication":
        this.manualApplicationModal = true;
        break;
      case "RemittanceSplit":
        this.remittanceSplitModal = true;
        break;
      case "ReversalTransaction":
        this.reverseTransactionModal = true;
        break;
      case "PostedClaimGLAdjustment":
        this.postedClaimGLAdjustmentModal = true;
        break;
      case "PostedRemittanceGLAdjustment":
        this.postedRemittanceGLAdjustmentModal = true;
        break;
      case "PostedRemittanceSEIDUpdate":
        this.postedRemittanceSEIDUpdateModal = true;
        break;
      case "PostedClaimReversal":
        this.postedClaimReversalModal = true;
        break;
    }
  }

  // End Actions functions

  onClaimsChange(event, posted) {
    let index = 0;
    let currentField: string = _.trim(
      event.originalEvent.currentTarget.innerText,
    );
    if (posted) {
      index = _.findIndex(
        this.tableHelper.PostedClaimsColumns,
        (o) => o.header === currentField,
      );
      this.tableHelper.showHideColumn(
        index,
        this.tableHelper.PostedClaimsColumns,
      );
    } else {
      index = _.findIndex(
        this.tableHelper.UnpostedClaimsColumns,
        (o) => o.header === currentField,
      );
      this.tableHelper.UnpostedClaimsColumns[index].show =
        !this.tableHelper.UnpostedClaimsColumns[index].show;
    }
  }

  onRemittancesChange(event, posted) {
    let index = 0;
    let currentField: string = _.trim(
      event.originalEvent.currentTarget.innerText,
    );
    if (posted) {
      index = _.findIndex(
        this.tableHelper.PostedRemittanceColumns,
        (o) => o.header === currentField,
      );
      this.tableHelper.PostedRemittanceColumns[index].show =
        !this.tableHelper.PostedRemittanceColumns[index].show;
    } else {
      index = _.findIndex(
        this.tableHelper.UnpostedRemittanceColumns,
        (o) => o.header === currentField,
      );
      this.tableHelper.UnpostedRemittanceColumns[index].show =
        !this.tableHelper.UnpostedRemittanceColumns[index].show;
    }
  }

  onTransHistoryChange(event, posted) {
    let index = 0;
    let currentField: string = _.trim(
      event.originalEvent.currentTarget.innerText,
    );
    index = _.findIndex(
      this.tableHelper.TransactionHistoryColumns,
      (o) => o.header === currentField,
    );
    this.tableHelper.TransactionHistoryColumns[index].show =
      !this.tableHelper.TransactionHistoryColumns[index].show;
  }

  async sortClaims(event, posted): Promise<void> {
    let sortOk: boolean;
    if (posted) {
      this.postedClaimsLoading = true;
      sortOk = this.postedClaims !== undefined;
    } else {
      this.unpostedClaimsLoading = true;
      sortOk = this.unpostedClaims !== undefined;
    }
    await this.getClaims(event.field, posted);
  }

  async sortRemittances(event, posted): Promise<void> {
    let sortOk: boolean;
    if (posted) {
      this.postedRemittancesLoading = true;
      sortOk = this.postedRemittances !== undefined;
    } else {
      this.unpostedRemittancesLoading = true;
      sortOk = this.unpostedRemittances !== undefined;
    }
    await this.getRemittances(event.field, posted);
  }

  async getAuditHistory(event, rowData: any, type: string) {
    this.callNotifyService();

    let refId = null;
    let cashOrClaim = null;
    switch (type) {
      case "postedClaim" || "unpostedClaim":
        refId = rowData.RawDataImport_ShardClaimIdentifier;
        cashOrClaim = "claim";
        break;
      case "postedRemittance" || "unpostedRemittance":
        refId = rowData.Payment_ReferenceNumber;
        cashOrClaim = "cash";
        break;
    }
    this.auditHistoryLoading = true;
    this.auditHistory = null;
    if (refId) {
      await this.searchService
        .getAuditHistory(
          this.globalFilterService.selectedEnvironment.ClientId4Digit,
          refId,
          cashOrClaim,
        )
        .then((data) => {
          this.auditHistory = data;
          this.auditHistoryLoading = false;
        })
        .catch((err) => {
          this.auditHistoryLoading = false;
        });
    } else {
      return false;
    }
  }

  async getAdjCode(event, rowIndex: number, posted: boolean) {
    this.callNotifyService();
    let row = null;
    this.adjustmentLoading = true;
    this.adjCodeDetailsVisible = true;
    // clean property
    this.adjustmentCodes = null;
    if (posted) {
      row = this.postedRemittances[rowIndex];
    } else {
      row = this.unpostedRemittances[rowIndex];
    }
    await this.searchService
      .getAdjCodeForRemittance(
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        row.Payment_Batch,
        row.Payment_Batch_Sequence,
        row.Payment_ReferenceNumber,
        posted,
      )
      .then((data) => {
        this.adjustmentCodes = this.transformAdjustmentCode(data);
        this.adjustmentLoading = false;
      })
      .catch((err) => {
        this.adjustmentLoading = false;
      });
  }

  async getClaims(
    sort?: string,
    posted: boolean = true,
    isAdvancedSearch: boolean = false,
  ): Promise<Claim[]> {
    this.callNotifyService();
    let dataResult: Claim[];
    await this.searchService
      .getServiceEvent<Claim>(
        EventSearchType.Claims as EventSearchType,
        this.eventIdModel.eventId,
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        sort,
        posted,
        isAdvancedSearch,
        this.advancedSearchCriteria,
      )
      .then((data) => {
        dataResult = data;
        if (posted) {
          this.postedClaimsLoading = false;
        } else {
          this.unpostedClaimsLoading = false;
        }
      })
      .catch((err) => {
        if (posted) {
          this.postedClaimsLoading = false;
        } else {
          this.unpostedClaimsLoading = false;
        }
      });

    return dataResult;
  }

  async getRemittances(
    sort?: string,
    posted: boolean = true,
    isAdvancedSearch: boolean = false,
  ): Promise<Remittance[]> {
    this.callNotifyService();
    let dataResult: Remittance[];

    await this.searchService
      .getServiceEvent<Remittance>(
        EventSearchType.Remittances as EventSearchType,
        this.eventIdModel.eventId,
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        sort,
        posted,
        isAdvancedSearch,
        this.advancedSearchCriteria,
      )
      .then((data) => {
        dataResult = data;
        if (posted) {
          this.postedRemittancesLoading = false;
        } else {
          this.unpostedRemittancesLoading = false;
        }
      })
      .catch((err) => {
        if (posted) {
          this.unpostedRemittancesLoading = false;
        } else {
          this.unpostedRemittancesLoading = false;
        }
      });

    return dataResult;
  }

  async getTransactionHistory(): Promise<ServiceEventTransactionHistoryItem[]> {
    this.callNotifyService();
    let dataResult: ServiceEventTransactionHistoryItem[];
    await this.searchService
      .getTransactionHistory(
        this.eventIdModel.eventId,
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
      )
      .then((data) => {
        dataResult = data;

        // set footer variables
        if (dataResult.length && dataResult.length > 0) {
          this.transHistoryARBalance =
            dataResult[dataResult.length - 1]["Money_ReceivableAmountAmount"];
          this.transHistoryUACBalance =
            dataResult[dataResult.length - 1]["Money_UnappliedAmount"];
        } else {
          this.transHistoryARBalance = null;
          this.transHistoryUACBalance = null;
        }

        this.transactionHistoryItemsLoading = false;
      })
      .catch((err) => {
        this.transactionHistoryItemsLoading = false;
      });

    return dataResult;
  }

  formatInvalidDate(date: string): string {
    if (_.includes(date, "0001")) {
      return undefined;
    }
    return date;
  }

  async onSubmit() {
    this.firstSearch = true;
    this.resetApplicationSelection();
    this.firstReversal = true;
    await this.loadData(this.eventIdModel.eventId).then(() => {
      this.updateLoadingState(false);
    });
  }

  reset(): void {
    this.callNotifyService();
    this.eventSearchForm.reset();
    this.eventId = "";
    this.cleanDataArrays();
    this.firstReversal = true;
  }

  cleanDataArrays(): void {
    this.postedClaims = [];
    this.postedRemittances = [];
    this.unpostedClaims = [];
    this.unpostedRemittances = [];

    this.transactionHistoryItems = [];

    this.transHistoryARBalance = null;
    this.transHistoryUACBalance = null;
    this.listOfReassigns = [];
  }

  updateLoadingState(state: boolean) {
    this.postedClaimsLoading = state;
    this.unpostedClaimsLoading = state;
    this.postedRemittancesLoading = state;
    this.unpostedRemittancesLoading = state;

    this.transactionHistoryItemsLoading = state;
  }

  get searchStatusRollup(): boolean {
    return (
      this.postedClaimsLoading ||
      this.unpostedClaimsLoading ||
      this.postedRemittancesLoading ||
      this.unpostedRemittancesLoading ||
      this.transactionHistoryItemsLoading
    );
  }

  dataAggregation() {
    return (
      this.postedClaims === undefined ||
      this.postedClaims.length > 0 ||
      this.unpostedClaims === undefined ||
      this.unpostedClaims.length > 0 ||
      this.postedRemittances === undefined ||
      this.postedRemittances.length > 0 ||
      this.unpostedRemittances === undefined ||
      this.unpostedRemittances.length > 0
    );
  }

  transformAdjustmentCode(data: any[]): any[] {
    // Note: Can't use the includes() function in this filter due to the fact
    // that several unwanted codes will be included in the case of 'includes('3')'
    let filteredCodes: any[] = _.filter(
      data,
      (o) =>
        o.Remittance_AdjustmentCode.trim() === "CLP03" ||
        o.Remittance_AdjustmentCode.trim() === "CLP04" ||
        o.Remittance_AdjustmentCode.trim() === "130" ||
        o.Remittance_AdjustmentCode.trim() === "3" ||
        o.Remittance_AdjustmentCode.trim() === "90" ||
        o.Remittance_AdjustmentCode.trim() === "91",
    );
    let codes = _.fromPairs(
      _.map(filteredCodes, (i) => [
        _.trim(i.Remittance_AdjustmentCode),
        i.Remittance_AdjustmentAmount,
      ]),
    );
    let others = this.transformAdjustmentOthers(
      _.difference(data, filteredCodes),
    );
    codes.Other = others;
    return [codes];
  }

  transformAdjustmentOthers(others: any[]): string {
    let result = [];
    _.forEach(others, (value, key) => {
      result.push(
        `(${value.Remittance_AdjustmentCode}: ${Number(value.Remittance_AdjustmentAmount).toLocaleString("en-US", { style: "currency", currency: "USD" })})`,
      );
    });
    return _.join(result, ",");
  }

  // called by get functions to update the timer every time an api is called
  private callNotifyService() {
    if (
      !this.authorizationService.HealthcareTopBar ||
      !this.authorizationService.HealthcareTopBar.notifyActivity
    ) {
      return;
    }
    this.authorizationService.HealthcareTopBar.notifyActivity();
  }

  showAdvancedSearch(show: boolean) {
    this.advancedSearchVisible = show;
  }

  async closeAdvancedSearch() {
    // hide advanced search
    this.advancedSearchVisible = false;
    // reset advanced search
    this.loadDefaultAdvancedSearchCriteria();
    // reload page
    await this.loadData(this.eventIdModel.eventId).then(() => {
      this.updateLoadingState(false);
    });
  }

  performAdvancedSearch() {
    // 1) validate that the number of days has been selected and  that at least one other criteria exist.
    // number of days is from a list so there has to be a value. No need to check.
    if (
      !this.advancedSearchCriteria.NCPDPMatch &&
      !this.advancedSearchCriteria.RxMatch &&
      !this.advancedSearchCriteria.CarrierOrCarrierGroupMatch
    ) {
      this.searchValidationError = true;
      this.searchValidationErrorMessage =
        "You must select at least on field to match on.";
    } else {
      // assure error is clear
      this.searchValidationError = false;
      this.searchValidationErrorMessage = "";

      // reset the official search values
      this.setSearchCriteriaCurrentState();
      // perform search
      this.loadAvancedSearchItems();
    }
  }
  private async loadAvancedSearchItems() {
    await this.loadData(this.eventIdModel.eventId, true).then(() => {
      this.updateLoadingState(false);
    });
  }

  setAdvancedSearchEnabled() {
    this.advancedSearchEnabled = this.atLeastTwoOfThree(
      this.advancedSearchCriteria.NCPDPMatch,
      this.advancedSearchCriteria.RxMatch,
      this.advancedSearchCriteria.CarrierOrCarrierGroupMatch,
    );
  }

  atLeastTwoOfThree(a: boolean, b: boolean, c: boolean): boolean {
    return (a && (b || c)) || (b && c);
  }

  postedClaimsContainsRelatedClaims(): boolean {
    return _.some(this.postedClaims, (pc) => pc.IsRelatedItem === true);
  }

  unpostedClaimsContainsRelatedClaims(): boolean {
    return _.some(this.unpostedClaims, (upc) => upc.IsRelatedItem === true);
  }

  postedRemittancesContainsRelatedRemittances(): boolean {
    return _.some(this.postedRemittances, (pr) => pr.IsRelatedItem === true);
  }

  unpostedRemittancesContainsRelatedRemittances(): boolean {
    return _.some(
      this.unpostedRemittances,
      (upr) => upr.IsRelatedItem === true,
    );
  }

  // Beginning of Reassign functionality.
  async reassignAdvancedSearchItems() {
    // 1) reassign the items that have been checked to this SEID
    await this.searchService
      .CashAndClaimsAssignment(
        this.eventIdModel.eventId,
        this.globalFilterService.selectedEnvironment.ClientId4Digit,
        this.listOfReassigns,
      )
      .then((data) => {
        this.rebaseSearchCriteria();
        this.listOfReassigns = [];
        this.loadAvancedSearchItems();
      });
  }

  relatedClaimItemChecked(event: any, data: Claim): void {
    if (event.target.checked) {
      this.listOfReassigns.push(
        new ServiceEventReassign(
          ServiceEventItemType.CLAIM,
          data.RawDataImport_ShardClaimIdentifier,
        ),
      );
    } else {
      _.remove(
        this.listOfReassigns,
        (o) => o.ReferenceNumber === data.RawDataImport_ShardClaimIdentifier,
      );
    }
  }

  relatedRemittanceItemChecked(event: any, data: Remittance): void {
    if (event.target.checked) {
      this.listOfReassigns.push(
        new ServiceEventReassign(
          ServiceEventItemType.CASH,
          data.Payment_ReferenceNumber,
        ),
      );
    } else {
      _.remove(
        this.listOfReassigns,
        (o) => o.ReferenceNumber === data.Payment_ReferenceNumber,
      );
    }
  }

  highlightRowYellow(rowData: any, rowIndex: number): string {
    if (rowData.IsRelatedItem === true) {
      return "inmar-highlight-yellow";
    }
  }
  // End of Reassign functionality.
}



// WEBPACK FOOTER //
// ./src/app/components/event-search/event-search.component.ts